// MyHomeMatrix UI string translations.
// Hinglish (Roman Hindi) tone: warm, conversational — like Indians actually speak.
// Keep technical English words (EMI, RERA, etc.) as-is.

export const T = {
  en: {
    appName: "MyHomeMatrix",
    chooseLanguage: "Choose your language",
    languageHelp: "You can change this anytime later.",
    english: "English",
    hinglish: "Hinglish (हिंदी / English)",
    continue: "Continue",
    back: "Back",
    skip: "Skip for now",
    next: "Next",

    // Opening
    openingTagline: "Find out what really matters in your home-buying decision.",
    openingSub: "In a few minutes, MyHomeMatrix helps you understand your personal home-buying priorities before you compare projects.",
    openingCta: "Start My Home Matrix",

    // Persona
    personaQuestion: "Who are you buying this home for?",
    confirmCta: "Show me what matters",

    // Top 4
    topTitle: "Choose the 4 benefits that matter most in this decision.",
    topSelected: (n) => `Selected ${n} of 4`,
    topMustPick4: "Please select exactly 4 top benefits.",
    topCta: "Continue with these 4",

    // Important 3
    importantTitle: "Now choose 3 more benefits that are important, but not your top priority.",
    importantHelp: (n) => `These matter, but they are not your strongest decision drivers. ${n} of 3 selected.`,
    importantMustPick3: "Please select exactly 3 important benefits.",

    // Flexible 2
    flexibleTitle: "Now choose 2 benefits where you are willing to be more flexible.",
    flexibleHelp: (n) => `These are not ignored. They matter, but you are more flexible about them compared to your top priorities. ${n} of 2 selected.`,
    flexibleMustPick2: "Please select exactly 2 flexible benefits.",

    // NDD
    nddTitle: "Good. You have now created clarity.",
    nddSub: "The remaining 3 benefits will not be treated as decision drivers for this purchase.\n\nThis does not mean they are bad or irrelevant. It simply means they should not confuse your decision.",
    nddSectionLabel: "Not Decision Drivers for This Purchase",
    nddCta: "See My Priority Summary",

    // Summary
    summaryTitle: "Your Home-Buying Priority Map",
    summarySub: "This is how your decision is currently shaped. Next, we will sharpen it further through a few trade-off questions.",
    summaryTop: "Top Priorities",
    summaryImportant: "Important",
    summaryFlexible: "Flexible",
    summaryNdd: "Not Decision Drivers",

    // Pairwise
    pairwiseTitle: "If you had to choose, what would matter more?",
    pairwiseStep: (i, n) => `Question ${i} of ${n}`,

    // Reflection
    reflectionTitle: "One last reflection.",
    reflectionStrongly: "Strongly",
    reflectionSomewhat: "Somewhat",
    reflectionNotReally: "Not really",

    // Budget
    budgetTitle: "Would you like to add your budget comfort?",
    budgetSub: "This is optional — it helps us tune your project matches.",
    budgetBuckets: {
      under_30k: "Under ₹30K / month",
      "30k_50k": "₹30K – ₹50K / month",
      "50k_75k": "₹50K – ₹75K / month",
      "75k_1l": "₹75K – ₹1L / month",
      "1l_1_5l": "₹1L – ₹1.5L / month",
      above_1_5l: "Above ₹1.5L / month",
    },

    // Capture
    captureTitle: "Where should we send your clarity report?",
    captureName: "Your name",
    capturePhone: "Phone (10-digit)",
    captureEmail: "Email (optional)",
    captureCity: "City",
    captureCta: "Generate My Report",
    captureMissing: "Please share your name and phone.",

    // Results
    resultsTitle: "Your Home-Buying Priority Fingerprint",
    resultsBuilding: "Building your priority map...",
    resultsTop3: "Top 3 Decision Drivers",
    resultsFull: "Full Priority Map",
    resultsHowToUse: "How to use this",
    resultsReportLoading: "Generating your personalised report...",
    resultsShare: "Share Report",
    resultsOpen: "Open Report",

    // Toasts
    errStart: "Could not start the session.",
    errSavePersona: "Could not save your buyer type.",
    errSaveTop: "Could not save your top priorities.",
    errSaveImportant: "Could not save the important benefits.",
    errSaveFlexible: "Could not finalize your bands.",
    errSavePairwise: "Could not save your trade-off answers.",
    errSaveReflection: "Could not record your response.",
    errCalculate: "Could not calculate your fingerprint.",
    errSaveCapture: "Could not save your details.",
    errLoadConfig: "Could not load MyHomeMatrix configuration.",
    shareCopied: "Report link copied!",
  },

  hi: {
    appName: "MyHomeMatrix",
    chooseLanguage: "Apni bhasha chuniye",
    languageHelp: "Aap baad mein kabhi bhi change kar sakte hain.",
    english: "English",
    hinglish: "Hinglish (हिंदी / English)",
    continue: "Aage badhein",
    back: "Pichhe",
    skip: "Abhi skip karein",
    next: "Aage",

    // Opening
    openingTagline: "Apne ghar ki kharidari mein kya sabse zaroori hai, woh samjhiye.",
    openingSub: "Kuch minute mein MyHomeMatrix aapko aapki personal home-buying priorities samjhayega — projects compare karne se pehle.",
    openingCta: "MyHomeMatrix Shuru Karein",

    // Persona
    personaQuestion: "Yeh ghar aap kiske liye le rahe hain?",
    confirmCta: "Dikhao kya zaroori hai",

    // Top 4
    topTitle: "Iss decision mein sabse zaroori 4 benefits chuniye.",
    topSelected: (n) => `${n} of 4 selected`,
    topMustPick4: "Please thik 4 top benefits chuniye.",
    topCta: "Iss 4 ke saath aage badhein",

    // Important 3
    importantTitle: "Ab 3 aur benefits chuniye jo important hain — lekin sabse top nahi.",
    importantHelp: (n) => `Ye matter karte hain, lekin aapke sabse strong decision drivers nahi hain. ${n} of 3 selected.`,
    importantMustPick3: "Please thik 3 important benefits chuniye.",

    // Flexible 2
    flexibleTitle: "Ab 2 benefits chuniye jin par aap thoda flexible reh sakte hain.",
    flexibleHelp: (n) => `In benefits ko ignore nahi kar rahe. Ye matter karte hain — lekin top priorities ke comparison mein aap thoda flexible hain. ${n} of 2 selected.`,
    flexibleMustPick2: "Please thik 2 flexible benefits chuniye.",

    // NDD
    nddTitle: "Bahut accha. Aapne clarity bana li.",
    nddSub: "Baaki ke 3 benefits iss purchase mein decision drivers nahi maane jayenge.\n\nIska matlab yeh nahi ki ye bekaar hain. Iska matlab itna hai ki ye aapke decision ko confuse na karein.",
    nddSectionLabel: "Iss Purchase ke liye Decision Drivers Nahi",
    nddCta: "Mera Priority Summary Dikhao",

    // Summary
    summaryTitle: "Aapka Home-Buying Priority Map",
    summarySub: "Aapka decision abhi aisa shape le raha hai. Aage hum kuch trade-off questions se ise aur sharpen karenge.",
    summaryTop: "Sabse Zaroori (Top Priorities)",
    summaryImportant: "Important",
    summaryFlexible: "Flexible",
    summaryNdd: "Decision Drivers Nahi",

    // Pairwise
    pairwiseTitle: "Agar aapko choose karna pade, toh kya zyada matter karega?",
    pairwiseStep: (i, n) => `Question ${i} of ${n}`,

    // Reflection
    reflectionTitle: "Ek aakhri reflection.",
    reflectionStrongly: "Bilkul",
    reflectionSomewhat: "Thoda thoda",
    reflectionNotReally: "Aisa nahi lagta",

    // Budget
    budgetTitle: "Kya aap apna budget comfort batayenge?",
    budgetSub: "Yeh optional hai — isse hum aapke project matches better tune kar sakte hain.",
    budgetBuckets: {
      under_30k: "₹30K / month se kam",
      "30k_50k": "₹30K – ₹50K / month",
      "50k_75k": "₹50K – ₹75K / month",
      "75k_1l": "₹75K – ₹1L / month",
      "1l_1_5l": "₹1L – ₹1.5L / month",
      above_1_5l: "₹1.5L / month se zyada",
    },

    // Capture
    captureTitle: "Aapki clarity report kahaan bheji jaye?",
    captureName: "Aapka naam",
    capturePhone: "Phone (10 digit)",
    captureEmail: "Email (optional)",
    captureCity: "Shahar (city)",
    captureCta: "Meri Report Banao",
    captureMissing: "Apna naam aur phone share kijiye.",

    // Results
    resultsTitle: "Aapka Home-Buying Priority Fingerprint",
    resultsBuilding: "Aapka priority map bana rahe hain...",
    resultsTop3: "Top 3 Decision Drivers",
    resultsFull: "Pura Priority Map",
    resultsHowToUse: "Iska istemaal kaise karein",
    resultsReportLoading: "Aapki personalised report bana rahe hain...",
    resultsShare: "Report Share Karein",
    resultsOpen: "Report Kholein",

    // Toasts
    errStart: "Session shuru nahi ho paya.",
    errSavePersona: "Aapka buyer type save nahi ho paya.",
    errSaveTop: "Top priorities save nahi ho payi.",
    errSaveImportant: "Important benefits save nahi ho paye.",
    errSaveFlexible: "Bands finalize nahi ho payi.",
    errSavePairwise: "Trade-off answers save nahi ho paye.",
    errSaveReflection: "Aapka response record nahi ho paya.",
    errCalculate: "Aapka fingerprint calculate nahi ho paya.",
    errSaveCapture: "Details save nahi ho payi.",
    errLoadConfig: "MyHomeMatrix configuration load nahi ho payi.",
    shareCopied: "Report ka link copy ho gaya!",
  },
};

export const useT = (lang) => T[lang] || T.en;
